import { assignInlineVars } from "@vanilla-extract/dynamic";
import clsx from "clsx";
import type React from "react";
import { forwardRef, type HTMLAttributes, useEffect, useRef } from "react";
import {
  type BadgeColor,
  type BadgeVariantStyle,
  badgeColorVariants,
  badgeDefaultColorClass,
  badgeInnerSpan,
  badgeVariants,
  colorBadgeContainer,
  colorVar,
  counterBadgeContainer,
  neutralBadgeContainer,
} from "./styles.css";

export type BadgeVariant = BadgeVariantStyle | "counter" | "neutral" | "color";

interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "size"> {
  customColor?: BadgeColor | string;
  variant?: BadgeVariant;
  size?: number; // for counter variant
}

const isThemeColor = (v: string): v is BadgeColor => v in badgeColorVariants;

// Resolve a class + optional inline-var override for the accent color slot.
function resolveAccent(customColor: BadgeColor | string | undefined) {
  if (!customColor) {
    return { className: badgeDefaultColorClass, style: undefined };
  }
  if (isThemeColor(customColor)) {
    return { className: badgeColorVariants[customColor], style: undefined };
  }
  return {
    className: undefined,
    style: assignInlineVars({
      [colorVar]: customColor,
    }) as React.CSSProperties,
  };
}

export const Badge = forwardRef(function Badge(
  {
    customColor = "primary",
    variant = "default",
    size = 20,
    children,
    className,
    ...props
  }: BadgeProps,
  ref: React.Ref<HTMLSpanElement>,
) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (variant === "counter" && spanRef.current) {
      const span = spanRef.current;
      const fontSize =
        Number(span.style.fontSize.replace("px", "")) || 0.5 * size;
      const spanWidth = span.getBoundingClientRect().width;
      const sizeWithPadding = size - 8;
      const proportion = sizeWithPadding / spanWidth;

      span.style.fontSize = `${Math.min(0.5 * size, proportion * fontSize)}px`;
    }
  }, [size, variant]);

  // Neutral variant — no accent color needed
  if (variant === "neutral") {
    return (
      <span
        className={clsx(neutralBadgeContainer, className)}
        ref={ref}
        {...props}
      >
        {children}
      </span>
    );
  }

  const accent = resolveAccent(customColor);

  // Counter variant
  if (variant === "counter") {
    return (
      <div
        className={clsx(counterBadgeContainer, accent.className, className)}
        ref={ref as React.Ref<HTMLDivElement>}
        style={{
          ...accent.style,
          width: `${size}px`,
          aspectRatio: "1" as const,
          fontSize: `${0.5 * size}px`,
          ...(props.style as React.CSSProperties),
        }}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      >
        <span
          className={badgeInnerSpan}
          ref={spanRef}
          style={{ fontSize: `${0.5 * size}px` }}
        >
          {children}
        </span>
      </div>
    );
  }

  // Color variant — soft tint bg with accent text
  if (variant === "color") {
    return (
      <span
        className={clsx(colorBadgeContainer, accent.className, className)}
        ref={ref}
        style={{
          ...accent.style,
          ...(props.style as React.CSSProperties),
        }}
        {...props}
      >
        {children}
      </span>
    );
  }

  // Default, outlined, tonal — composed of variant class + color class
  return (
    <span
      className={clsx(badgeVariants[variant], accent.className, className)}
      ref={ref}
      style={{
        ...accent.style,
        ...(props.style as React.CSSProperties),
      }}
      {...props}
    >
      {children}
    </span>
  );
});

Badge.displayName = "Badge";
